import { Component, type ErrorInfo, type ReactNode } from "react";

import { logger } from "@/lib/logger";
import { Button } from "@/ui/Button";
import { ErrorState } from "@/ui/states";

interface Props {
  children: ReactNode;
  /** Identifies the boundary in logs. Never user data. */
  boundary: string;
  fallback?: (reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render errors so a fault in one screen does not blank the whole
 * application. The message is deliberately not rendered: an exception can carry
 * arbitrary interpolated content, and this application must not display or log
 * anything that might have come from clinical data.
 */
export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error("Unhandled render error", {
      component: this.props.boundary,
      errorName: error.name,
      // componentStack is a component-name trace, never user content.
      reason: info.componentStack ? "component-stack-available" : "no-stack",
    });
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (!this.state.error) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback(this.reset);
    }

    return (
      <ErrorState
        title="This screen could not be displayed"
        description="The error has been logged. Try again, or reload the application if it persists."
        onRetry={this.reset}
      />
    );
  }
}

export function ReloadButton() {
  return (
    <Button variant="outline" onClick={() => window.location.reload()}>
      Reload the application
    </Button>
  );
}
